import React, { useState, useEffect, useMemo, useRef } from "react";
import { useOutletContext } from 'react-router-dom';
import {
    Box, Card, CardContent, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Typography, Checkbox,
    TablePagination, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, TableSortLabel, CircularProgress, Container, Alert,
    FormControlLabel
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSelector, useDispatch } from 'react-redux';

import * as egressApi from "../api/egressAPI";
import useAuth from '../hooks/useAuth';
import usePrivileges from '../hooks/usePrivileges';
import usePageTitle from "../hooks/usePageTitle";
import { parseApiError } from "../utils/errorUtils";
import { fetchEgresses } from '../app/reducers/dataCacheSlice';
import OutputIcon from '@mui/icons-material/Output';

const headCells = [
    { id: 'type', label: 'Type' },
    { id: 'path', label: 'Path ID Name' },
    { id: 'config', label: 'Config' },
];

export default function DAAC() {
    usePageTitle("DAAC Egress");

    const dispatch = useDispatch();
    const { activeNgroupId } = useAuth();
    const { hasPrivilege } = usePrivileges();
    const { setMenuItems } = useOutletContext();

    // Get data from the central Redux cache
    const { egresses } = useSelector((state) => state.dataCache);

    // Local state for UI operations
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(0); // 0-based for MUI TablePagination
    const pagination = {
        page: currentPage,
        pageSize: rowsPerPage,
        total: egresses.total ?? 0
    };
    const [sorting, setSorting] = useState({ orderBy: 'type', order: 'asc' });
    const [searchTerm, setSearchTerm] = useState('');
    const [dialog, setDialog] = useState({ open: null, data: null });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [filteredCount, setFilteredCount] = useState(0);
    const didMount = useRef(false);
    const [prevPage, setPrevPage] = useState(0);
    const wasSearching = useRef(false);

    useEffect(() => {
        const daacMenuItems = [{ text: 'Egress', path: '/daac', icon: <OutputIcon /> }];
        setMenuItems(daacMenuItems);
        return () => setMenuItems([]);
    }, [setMenuItems]);

    // "Smart" data fetching that uses the cache
    useEffect(() => {
        if (activeNgroupId && !didMount.current) {
            if (egresses.status === 'idle' ||  egresses.page !== 1){
                dispatch(fetchEgresses({ page: 1, pageSize: 50 }));
            }
            didMount.current = true;
        }
    }, [activeNgroupId, egresses.status, dispatch]);

    // Derives the page's loading state from the cache status
    useEffect(() => {
        setLoading(egresses.status === 'loading');
    }, [egresses.status]);

    useEffect(() => {
        setCurrentPage(0);
    }, [searchTerm]);

    // Reset pagination when ngroup changes or when new collections load
    useEffect(() => {
        const maxPage = Math.floor((egresses.total - 1) / rowsPerPage) || 0;

        // if currentPage is invalid, reset to 0
        if (currentPage > maxPage) {
            setCurrentPage(0);
        }
    }, [activeNgroupId, egresses.total]);

    useEffect(() => {
            const isSearching = searchTerm.trim() !== "";
    
            if (isSearching && !wasSearching.current) {
                // Search just started — store page ONCE
                setPrevPage(currentPage);
                setCurrentPage(0);
            }
    
            if (!isSearching && wasSearching.current) {
                // Search just ended — restore ONCE
                setCurrentPage(prevPage);
            }
    
            wasSearching.current = isSearching;
        }, [searchTerm, currentPage, prevPage]);

    const processedEgresses = useMemo(() => {
        let filtered = [...egresses.data];
        if (searchTerm) {
            const lowercasedTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(e =>
                e.type.toLowerCase().includes(lowercasedTerm) ||
                e.path.toLowerCase().includes(lowercasedTerm)
            );
        }

        filtered.sort((a, b) => {
            const isAsc = sorting.order === 'asc' ? 1 : -1;
            const aValue = a[sorting.orderBy] || '';
            const bValue = b[sorting.orderBy] || '';
            return aValue.localeCompare(bValue) * isAsc;
        });
        const newFilteredCount = filtered.length;

        if (newFilteredCount !== filteredCount) {
            setFilteredCount(newFilteredCount);
        }
        
        let startIndex, endIndex;

        if (searchTerm) {
            // SCENARIO 1: SEARCHING/FILTERING
            // List contains the *entire* locally filtered/sorted set.
            const maxFilteredPage = Math.max(
                0,
                Math.floor((filtered.length - 1) / rowsPerPage)
            );

            const safeSearchPage = Math.min(currentPage, maxFilteredPage);

            startIndex = safeSearchPage * rowsPerPage;
            endIndex = startIndex + rowsPerPage;
        } else {
            // SCENARIO 2: NORMAL VIEW (PAGINATING THROUGH CACHE CHUNKS)
            startIndex = Math.max(0, pagination.page * rowsPerPage - (egresses.cacheStart ?? 0));
            endIndex = Math.min(filtered.length, startIndex + rowsPerPage);
        }
        return filtered.slice(startIndex, endIndex);
    }, [egresses.data, sorting, searchTerm, pagination.page, rowsPerPage]);

    const handleOpenDialog = (type, data = null) => {
        if (type === 'edit') {
            const egressToEdit = egresses.data.find(e => e.id === selected[0]);

            // Extract prefix_username and create_collection_subfolder from the egress config
            const configObj = { ...egressToEdit.config };
            const prefixUsername = configObj.prefix_username === "true" || configObj.prefix_username === true;
            const createCollectionSubfolder = configObj.create_collection_subfolder === "true" || configObj.create_collection_subfolder === true;

            // Delete these keys so they are not shown in the JSON config text area
            delete configObj.prefix_username;
            delete configObj.create_collection_subfolder;

            const prettyConfig = JSON.stringify(configObj, null, 2);
            setDialog({
                open: 'edit',
                data: {
                    ...egressToEdit,
                    prefix_username: prefixUsername,
                    create_collection_subfolder: createCollectionSubfolder,
                    config: prettyConfig
                }
            });
        } else if (type === 'add') {
            setDialog({
                open: 'add',
                data: {
                    type: "",
                    path: "",
                    prefix_username: false,
                    create_collection_subfolder: false,
                    config: "{\n  \n}"
                }
            });
        } else {
            setDialog({ open: type, data });
        }
    };

    const handleCloseDialog = () => setDialog({ open: null, data: null });

    const folderStructurePreview = useMemo(() => {
        let bucket = "<bucket-name>";
        let destination = "<destination-path>";
        try {
            const parsed = JSON.parse(dialog.data?.config || "{}");
            if (parsed.bucket) bucket = parsed.bucket;
            if (parsed['destination-path']) destination = parsed['destination-path'];
        } catch (e) {
            // fallback
        }

        const prefixUsernameValue = dialog.data?.prefix_username || false;
        const createCollectionSubfolderValue = dialog.data?.create_collection_subfolder || false;

        const parts = [bucket];
        if (destination && destination !== "<destination-path>") {
            parts.push(destination);
        }
        if (createCollectionSubfolderValue) {
            parts.push("<collection>");
        }
        if (prefixUsernameValue) {
            parts.push("<username>");
        }
        parts.push("<filename>");
        return parts.join("\\");
    }, [dialog.data?.config, dialog.data?.prefix_username, dialog.data?.create_collection_subfolder]);

    const handleCheckboxChange = (name, checked) => {
        setDialog(prev => ({
            ...prev,
            data: {
                ...prev.data,
                [name]: checked
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        let parsedConfig;
        try {
            parsedConfig = JSON.parse(dialog.data.config);
        } catch (parseError) {
            toast.error(`Invalid JSON in config: ${parseError.message}`);
            setIsSubmitting(false);
            return;
        }

        try {
            const payload = {
                type: dialog.data.type,
                path: dialog.data.path,
                config: {
                    ...parsedConfig,
                    prefix_username: dialog.data.prefix_username ? "true" : "false",
                    create_collection_subfolder: dialog.data.create_collection_subfolder ? "true" : "false"
                }
            };
            if (dialog.open === 'add') {
                await egressApi.createEgress(payload);
                toast.success("Egress added successfully!");
            } else if (dialog.open === 'edit') {
                await egressApi.updateEgress(dialog.data.id, payload);
                toast.success("Egress updated successfully!");
            }
            handleCloseDialog();
            setSelected([]);
            const apiPage = Math.floor((currentPage * rowsPerPage) / 50) + 1;
            dispatch(fetchEgresses({ page: apiPage, pageSize: 50 })); // Refresh the cache
        } catch (error) {
            toast.error(parseApiError(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleConfirmDelete = async () => {
        setIsSubmitting(true);
        try {
            await Promise.all(selected.map(egressId => egressApi.deleteEgress(egressId)));
            toast.success("Egress deleted successfully!");
            handleCloseDialog();
            setSelected([]);
            const apiPage = Math.floor((currentPage * rowsPerPage) / 50) + 1;
            dispatch(fetchEgresses({ page: apiPage, pageSize: 50 })); // Refresh the cache
        } catch (error) {
            toast.error(parseApiError(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRequestSort = (property) => {
        const isAsc = sorting.orderBy === property && sorting.order === 'asc';
        setSorting({ order: isAsc ? 'desc' : 'asc', orderBy: property });
    };

    const handleSelectAllClick = (event) => setSelected(event.target.checked ? processedEgresses.map(e => e.id) : []);

    const handleClick = (id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];
        if (selectedIndex === -1) newSelected = [...selected, id];
        else newSelected = selected.filter(selId => selId !== id);
        setSelected(newSelected);
    };

    const isWithinCache = (newPage) => {
        if (egresses.total <= egresses.cacheSize) {
            return true;
        }
        const startIndex = newPage * rowsPerPage;// use UI rowsPerPage
        const endIndex = startIndex + rowsPerPage;

        const cacheStart = egresses.cacheStart ?? 0;
        const cacheEnd = cacheStart + (egresses.cacheSize ?? 0);  // usually 50

        return startIndex >= cacheStart && endIndex <= cacheEnd;
    };

    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage); // Pagination.page gets updated

        if (!isWithinCache(newPage)) {
            const apiPageSize = 50; // chunk size
            const startIndex = newPage * rowsPerPage;
            if (!searchTerm){
                const apiPage = Math.floor(startIndex / apiPageSize) + 1;
                dispatch(fetchEgresses({ page: apiPage, pageSize: apiPageSize }));
            }
        }
    };

    const handleRowsPerPageChange = (event) => {
        const newSize = parseInt(event.target.value, 10);
        setCurrentPage(0);//bring back the page to 0 when the rowsperpage change
        if (!isWithinCache(0)) {
            const apiPageSize = 50; // chunk size
            const startIndex = 0;
            if (!searchTerm){
                const apiPage = Math.floor(startIndex / apiPageSize) + 1;
                dispatch(fetchEgresses({ page: apiPage, pageSize: apiPageSize }));
            }
        }
        setRowsPerPage(newSize);  // only update UI rows per page
    };

    return (
        <Container maxWidth={false} disableGutters>
            <ToastContainer position="top-center" />
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                        <Typography variant="h5">Egress</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField label="Search" variant="outlined" size="small" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                            <Button variant="contained" color="primary" onClick={() => handleOpenDialog('add')} startIcon={<AddIcon />} disabled={!hasPrivilege('egress:create')}>Add</Button>
                            <Button variant="contained" onClick={() => handleOpenDialog('edit')} disabled={selected.length !== 1 || !hasPrivilege('egress:update')} startIcon={<EditIcon />}>Edit</Button>
                            <Button variant="contained" color="error" onClick={() => handleOpenDialog('delete')} disabled={selected.length === 0 || !hasPrivilege('egress:delete')} startIcon={<DeleteIcon />}>Delete</Button>
                        </Box>
                    </Box>

                    {egresses.error && <Alert severity="error" sx={{ my: 2 }}>{egresses.error}</Alert>}

                    <TableContainer component={Paper}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ bgcolor: "#E5E8EB" }} padding="checkbox"><Checkbox indeterminate={selected.length > 0 && selected.length < processedEgresses.length} checked={processedEgresses.length > 0 && selected.length === processedEgresses.length} onChange={handleSelectAllClick} /></TableCell>
                                    {headCells.map(headCell => (
                                        <TableCell sx={{ bgcolor: "#E5E8EB" }} key={headCell.id} sortDirection={sorting.orderBy === headCell.id ? sorting.order : false}>
                                            <TableSortLabel active={sorting.orderBy === headCell.id} direction={sorting.orderBy === headCell.id ? sorting.order : 'asc'} onClick={() => handleRequestSort(headCell.id)}>
                                                {headCell.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5 }}><CircularProgress /></TableCell></TableRow>
                                ) : processedEgresses.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            <Typography sx={{ py: 5 }} color="text.secondary">
                                                {searchTerm ? "No Egress targets match your search." : "No Egress found."}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    processedEgresses.map((row) => {
                                        const isItemSelected = selected.indexOf(row.id) !== -1;
                                        return (
                                            <TableRow key={row.id} hover onClick={() => handleClick(row.id)} role="checkbox" tabIndex={-1} selected={isItemSelected}>
                                                <TableCell padding="checkbox"><Checkbox checked={isItemSelected} /></TableCell>
                                                <TableCell>{row.type}</TableCell>
                                                <TableCell>{row.path}</TableCell>
                                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{JSON.stringify(row.config)}</TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={searchTerm ? filteredCount : pagination.total}
                        rowsPerPage={pagination.pageSize}
                        page={pagination.page}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                    />
                </CardContent>
            </Card>

            <Dialog open={dialog.open === 'add' || dialog.open === 'edit'} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle>{dialog.open === 'add' ? 'Add Egress' : 'Edit Egress'}</DialogTitle>
                <Box component="form" onSubmit={handleSubmit}>
                    <DialogContent>
                        {dialog.data && (
                            <>
                                <TextField autoFocus margin="dense" name="type" label="Type" fullWidth value={dialog.data.type} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, type: e.target.value } })} required />
                                <TextField margin="dense" name="path" label="Path ID" fullWidth value={dialog.data.path} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, path: e.target.value } })} required />
                                <TextField margin="dense" name="config" label="Config (JSON format)" fullWidth multiline rows={5} value={dialog.data.config} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, config: e.target.value } })} required />

                                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!dialog.data.create_collection_subfolder}
                                                onChange={(e) => handleCheckboxChange("create_collection_subfolder", e.target.checked)}
                                            />
                                        }
                                        label="Upload files to a collection-name subfolder"
                                    />

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!dialog.data.prefix_username}
                                                onChange={(e) => handleCheckboxChange("prefix_username", e.target.checked)}
                                            />
                                        }
                                        label="Upload files to a provider-username subfolder"
                                    />
                                </Box>

                                {(dialog.data.prefix_username || dialog.data.create_collection_subfolder) && (
                                    <Box sx={{ mt: 2 }}>
                                        <Alert severity="info">
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                Folder Structure Preview:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mt: 0.5 }}>
                                                {folderStructurePreview}
                                            </Typography>
                                        </Alert>
                                    </Box>
                                )}
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={isSubmitting}>
                            {isSubmitting ? <CircularProgress size={24} /> : (dialog.open === 'add' ? 'Add' : 'Save')}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

            <Dialog open={dialog.open === 'delete'} onClose={handleCloseDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>Are you sure you want to delete the {selected.length} selected egress target(s)?</DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={isSubmitting}>
                        {isSubmitting ? <CircularProgress size={24} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}